import Seo from '@/components/Seo';
import { useAdSuppression } from '@/hooks/useAdSuppression';

export default function AboutPage() {
    useAdSuppression();
    return (
        <>
            <Seo
                title="About us"
                description="UtilityTool is a small collection of high-quality online utility tools — built with care, kept fast, and offered without signups or paywalls."
                canonical="/about"
            />
            <section className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 pt-14 pb-16">
                <p className="text-sm text-slate-500">About us</p>
                <h1 className="mt-1 text-3xl sm:text-4xl font-semibold tracking-tight text-slate-900">
                    Small tools, made with care.
                </h1>
                <p className="mt-3 text-slate-600 text-lg">
                    UtilityTool exists because we couldn’t find a home for the small utilities we
                    reach for every week — file converters, image squishers, quick text helpers
                    — that felt calm, trustworthy, and free of ad-tech clutter.
                </p>
                <div className="prose mt-8">
                    <h2>What we care about</h2>
                    <p>
                        A UtilityTool tool has to do three things well: it has to work reliably on
                        the file you give it, it has to be fast enough that you don’t reach for a
                        desktop app instead, and it has to be respectful of your time and privacy.
                    </p>
                    <p>
                        We measure ourselves against those three things and nothing else. If a
                        feature makes a tool worse on any of them, we don’t ship it, even if it
                        would earn us more money.
                    </p>
                    <h2>How we make money</h2>
                    <p>
                        UtilityTool is supported by unobtrusive, contextual ads. That’s it. There’s
                        no premium tier that unlocks the “real” version of a tool, no bundle you
                        get pushed into, and no watermark on the files we hand back to you.
                    </p>
                    <p>
                        If you’d like an ad-free experience, most modern browsers let you disable
                        ads on a per-site basis. We don’t block that. We’d rather earn your
                        goodwill than force pageviews.
                    </p>
                    <h2>How we handle your files</h2>
                    <p>
                        Files uploaded for processing are handled in an isolated worker process
                        on our servers and deleted from disk immediately after conversion. We
                        don’t keep copies, we don’t log the contents, and we don’t reuse them for
                        any purpose.
                    </p>
                    <p>
                        For tools that don’t need a server round-trip — things like the word
                        counter or QR code generator — we do the work in your browser and never
                        send your input to us at all.
                    </p>
                    <h2>Who we are</h2>
                    <p>
                        We’re a very small team of engineers who like small, well-made things.
                        You can reach us at{' '}
                        <a href="mailto:hello@utilitytool.co.za">hello@utilitytool.co.za</a>. We answer
                        every email personally, and we don’t use auto-responders.
                    </p>
                    <h2>Where we’re based</h2>
                    <p>
                        UtilityTool is operated from Johannesburg, South Africa. Our
                        registered contact address is:
                    </p>
                    <p>
                        <strong>UtilityTool</strong><br />
                        Route 82 Security Apartments, Unit 34<br />
                        Vereeniging Rd, Olifantsvlei 327-Iq<br />
                        Johannesburg South, 2091<br />
                        South Africa
                    </p>
                    <p>
                        Thanks for using UtilityTool. We hope one of our tools makes your day a
                        little easier.
                    </p>
                </div>
            </section>
        </>
    );
}
